import { placeActions } from '@/actions';
import LoadingDotsBlack from '@/components/animations/LoadingDotsBlack/LoadingDotsBlack';
import PlaceItemCard from '@/components/elements/Card/PlaceItemCard/PlaceItemCard';
import SearchInput from '@/components/elements/forms/SearchInput/SearchInput';
import { Fab } from '@/components/elements/forms/buttons';
import Icon from '@/components/icons/Icon';
import { MapTiler } from '@/components/modules/map';
import { DynamicMapProps } from '@/components/modules/map/MapTiler';
import SearchModal from '@/components/modules/modals/SearchModal';
import NavBar from '@/components/modules/navigation/navbar/NavBar/NavBar';
import EmptyState from '@/components/templates/EmptyState';
import { Z_INDEX } from '@/constants/styleConstants';
import { trackPlaceClick } from '@/features/searchV2/helpers/tracking';
import {
  getPlaceImage,
  hasFeatureSetting,
  isPlaceInBounds,
  isPlaceOpenToday,
  isServer,
  isSistaminuten,
  salonTracking,
  setBookingStartingPoint,
  setWhitelabelSession,
  trackPage,
} from '@/helpers';
import { useAppSelector } from '@/hooks';
import { useSearchContext } from '@/hooks/search/useSearch';
import useMobileView from '@/hooks/useMobileView';
import { _s } from '@/locale';
import { PLACE_IMPRESSION, placeService } from '@/services';
import { getPortalPlaces } from '@/services/portalPlaceServices';
import { SearchPlace } from '@/types/api/services/search';
import { PortalPlace } from '@/types/portal';
import { Dispatch, ReactNode, createContext, useContext, useEffect, useReducer, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';

type SearchState = {
  places: PortalPlace[];
  filteredPlaces: PortalPlace[];
  loadingPlaces: boolean;
  initialLoad: boolean;
  suggestions: string[];
  searchValue: string;
  searchBounds?: string;
};

type SearchAction =
  | { type: 'SET_PLACES'; payload: PortalPlace[] }
  | { type: 'SET_FILTERED_PLACES'; payload: PortalPlace[] }
  | { type: 'SET_LOADING_PLACES'; payload: boolean }
  | { type: 'SET_SEARCH_VALUE'; payload: string }
  | { type: 'SET_SEARCH_BOUNDS'; payload: string };

function getFilteredPlacesFromSearch(places: PortalPlace[], searchValue: string, bounds?: string) {
  if (!Array.isArray(places)) {
    return [];
  }

  return places.filter((place) => {
    const positionValid = isPlaceInBounds(place?.contact?.position, bounds);
    const nameMatches = place?.about?.name.toLowerCase().includes(searchValue.toLowerCase());
    const cityMatches = place?.contact?.address?.city.toLowerCase().includes(searchValue.toLowerCase());
    return positionValid && (nameMatches || cityMatches);
  });
}

async function fetchAndSetPlaces({ id, group }: { id: number; group?: string }, dispatch: Dispatch<SearchAction>) {
  const { places } = await getPortalPlaces({ id, group });
  dispatch({ type: 'SET_PLACES', payload: places });
  (places || []).forEach((place) => {
    salonTracking(place);
  });

  return places;
}

const searchReducer = (state: SearchState, action: SearchAction): SearchState => {
  switch (action.type) {
    case 'SET_PLACES':
      const suggestions = (action.payload || [])
        .filter((place) => place?.contact?.address?.city)
        .map((place) => place.contact.address.city)
        .map((city) =>
          city
            .split(' ')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' '),
        )
        .filter((city) => city !== '')
        .filter((city, index, self) => self.indexOf(city) === index);

      return { ...state, places: action.payload, suggestions, loadingPlaces: false, initialLoad: false };
    case 'SET_FILTERED_PLACES':
      return { ...state, filteredPlaces: action.payload, loadingPlaces: false };
    case 'SET_LOADING_PLACES':
      return { ...state, loadingPlaces: action.payload };
    case 'SET_SEARCH_VALUE':
      return { ...state, searchValue: action.payload };
    case 'SET_SEARCH_BOUNDS':
      return { ...state, searchBounds: action.payload };
    default:
      return state;
  }
};

const usePortalPageManager = (): {
  search: SearchState;
  handleSearchValueChange: (value: string) => void;
  handleSearchBoundsChange: (value: string) => void;
  setLoadingPlaces: (loading: boolean) => void;
} => {
  const [search, searchDispatch] = useReducer(searchReducer, {
    places: [],
    filteredPlaces: [],
    suggestions: [],
    loadingPlaces: false,
    initialLoad: true,
    searchValue: '',
  });

  const { params } = useRouteMatch() as { params: { id: string; group: string } };
  const { id, group } = params;

  useEffect(() => {
    fetchAndSetPlaces({ id: Number(id), group }, searchDispatch);
  }, [id, group]);

  useEffect(() => {
    searchDispatch({
      type: 'SET_FILTERED_PLACES',
      payload: getFilteredPlacesFromSearch(search.places, search.searchValue, search.searchBounds),
    });
  }, [search.searchValue, search.places, search.searchBounds]);

  function handleSearchValueChange(value: string) {
    searchDispatch({ type: 'SET_SEARCH_VALUE', payload: value });
  }

  function handleSearchBoundsChange(bounds: string) {
    searchDispatch({ type: 'SET_SEARCH_BOUNDS', payload: bounds });
  }

  function setLoadingPlaces(loading: boolean) {
    searchDispatch({ type: 'SET_LOADING_PLACES', payload: loading });
  }

  return {
    search,
    handleSearchValueChange,
    handleSearchBoundsChange,
    setLoadingPlaces,
  };
};

export type PortalPageContextType = ReturnType<typeof usePortalPageManager>;

export const PortalPageContext = createContext<PortalPageContextType>({
  search: {
    initialLoad: true,
    places: [],
    filteredPlaces: [],
    loadingPlaces: false,
    suggestions: [],
    searchValue: '',
  },
  handleSearchValueChange: () => null,
  handleSearchBoundsChange: () => null,
  setLoadingPlaces: () => null,
});

export const PortalPageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  return <PortalPageContext.Provider value={usePortalPageManager()}>{children}</PortalPageContext.Provider>;
};

const Portal = () => {
  const searchManager = useSearchContext();
  const { search, handleSearchValueChange, handleSearchBoundsChange } = useContext(PortalPageContext);
  const [mobileSearch, setMobileSearch] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const { isMobileView } = useMobileView();
  const history = useHistory();
  const { params } = useRouteMatch() as { params: { id: string; slug: string } };

  useEffect(() => {
    trackPage();
    localStorage.setItem('isLanding', params.id);
    localStorage.setItem('slug', params.slug);
    if (isServer) return;
    window.scrollTo(0, 0);
  }, []);

  const seo = () => {
    const isNoIndex = hasFeatureSetting(search?.filteredPlaces?.[0], 'seo_noindex_portal') || isSistaminuten();
    return <Helmet>{isNoIndex && <meta name="robots" content="noindex" />}</Helmet>;
  };

  const mapProps: DynamicMapProps = {
    places: search.filteredPlaces,
    fetching: search.loadingPlaces,
    mapTopPos: '64px',
    onMapBoundsChange: (bounds: string) => handleSearchBoundsChange(bounds),
    onPlaceClick: (place: SearchPlace, carouselIndex: number) => {
      trackPlaceClick(searchManager, place, carouselIndex, 'search_results_map');
      setBookingStartingPoint('search_results', searchManager.q);
      placeService.storePlaceImpressions([place.id], PLACE_IMPRESSION.SERP_TO_PLACE_CLICK);
    },
  };

  return (
    <>
      <NavBar back onBackButtonClick={() => history.goBack()} title={_s('portalPage.title')} type="subView" />
      <div className="bg-gradient flex min-h-[calc(100vh-64px)]">
        {isMobileView ? (
          <>
            {showMap ? (
              <MapTiler {...mapProps} carouselClassname="!bottom-[94px]" mapWidth="!w-full" />
            ) : (
              <PlacesList setMobileSearch={setMobileSearch} />
            )}
            <SearchModal
              isOpen={mobileSearch}
              suggestions={search.suggestions}
              onChange={(_, { newValue }) => {
                handleSearchValueChange(newValue);
              }}
              placeholder={_s('portalPage.searchModal.placeholder')}
              title={_s('portalPage.searchModal.title')}
              value={search.searchValue}
              onSuggestionSelected={() => setMobileSearch(false)}
              onClose={() => setMobileSearch(false)}
            />
          </>
        ) : (
          <>
            <PlacesList setMobileSearch={setMobileSearch} />
            <MapTiler {...mapProps} mapWidth="!w-[calc(100%-390px)]" />
          </>
        )}
        {isMobileView && (
          <div className="fixed bottom-0 flex w-full justify-center pb-[30px]" style={{ zIndex: Z_INDEX.FAB_PORTAL }}>
            <Fab
              onClick={() => setShowMap((prev) => !prev)}
              size="md"
              rightIcon={<Icon variant={showMap ? 'list-ul' : 'map'} />}
              label={showMap ? _s('portalPage.showList') : _s('portalPage.showMap')}
              floating
            />
          </div>
        )}
      </div>
      {seo()}
    </>
  );
};

const PlacesList = ({ setMobileSearch }: { setMobileSearch: Dispatch<React.SetStateAction<boolean>> }) => {
  const { search, handleSearchValueChange, handleSearchBoundsChange, setLoadingPlaces } = useContext(PortalPageContext);
  const dispatch = useDispatch();
  const { isMobileView } = useMobileView();
  const isWhitelabel = useAppSelector((state) => state?.settings.isWhitelabel);
  setWhitelabelSession(isWhitelabel);
  const handleSearchInputChange = (_, { newValue }) => {
    setLoadingPlaces(true);
    handleSearchValueChange(newValue);
    handleSearchBoundsChange(undefined);
  };

  const handleSearchInputRemove = () => {
    setLoadingPlaces(true);
    handleSearchValueChange('');
    handleSearchBoundsChange(undefined);
  };

  const handleSearchInputFocus = (e) => {
    if (!isMobileView) return;
    e.target.blur() || setMobileSearch(true);
  };

  const handleSearchInputSuggestionSelected = () => {
    handleSearchBoundsChange(undefined);
  };

  return (
    <div className={`gap-md p-lg flex w-full flex-col ${isMobileView ? '' : 'max-w-[390px]'}`}>
      <SearchInput
        suggestions={search.suggestions}
        onChange={handleSearchInputChange}
        onRemove={handleSearchInputRemove}
        onFocus={handleSearchInputFocus}
        onSuggestionSelected={handleSearchInputSuggestionSelected}
        placeholder={_s('portalPage.searchInput')}
        value={search.searchValue}
      />
      {search.filteredPlaces.length ? (
        search.filteredPlaces.map((place) => {
          if (place && place.about) {
            const imageUrl = getPlaceImage(place);
            return (
              <div
                onMouseOver={() => dispatch(placeActions.setHover(place.id))}
                onMouseOut={() => dispatch(placeActions.removeHover())}>
                <PlaceItemCard
                  key={place.id}
                  src={{
                    pathname: `/places/${place.about?.slug}-${place.id}`,
                    state: { fromList: true },
                  }}
                  address={{
                    city: place.contact?.address?.city,
                    postal: place.contact?.address?.zipcode,
                    street: place.contact?.address?.street,
                  }}
                  businessHours={isPlaceOpenToday(place)}
                  rating={place?.rating?.score}
                  ratingCount={place?.rating?.count}
                  title={place.about?.name}
                  image={{ src: imageUrl }}
                />
              </div>
            );
          }
          return null;
        })
      ) : !search.loadingPlaces && !search.initialLoad ? (
        <div className="flex-1">
          <div>
            <EmptyState
              body={_s('portalPage.emptyState.content')}
              icon={<Icon color="black-200" style={{ width: 128 }} variant="search" />}
              title={_s('portalPage.emptyState.title')}
              containerClassName="!min-h-full"
            />
          </div>
        </div>
      ) : (
        <div className="flex flex-1 items-center justify-center">
          <LoadingDotsBlack loop onAnimationComplete={() => {}} />{' '}
        </div>
      )}
    </div>
  );
};

export default function PortalPage(props) {
  return (
    <PortalPageProvider>
      <Portal {...props} />
    </PortalPageProvider>
  );
}
